package it.maicol07.gamerlogue.ui.views.library.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledIconToggleButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.SheetValue
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import at.released.igdbclient.model.Game
import gamerlogue.sharedui.generated.resources.Res
import gamerlogue.sharedui.generated.resources.library__add_to_library
import gamerlogue.sharedui.generated.resources.library__cancel
import gamerlogue.sharedui.generated.resources.library__clear_review_confirm
import gamerlogue.sharedui.generated.resources.library__clear_review_message
import gamerlogue.sharedui.generated.resources.library__clear_review_title
import gamerlogue.sharedui.generated.resources.library__completion_status
import gamerlogue.sharedui.generated.resources.library__completion_status_description
import gamerlogue.sharedui.generated.resources.library__delete
import gamerlogue.sharedui.generated.resources.library__edit_entry
import gamerlogue.sharedui.generated.resources.library__save
import gamerlogue.sharedui.generated.resources.library__save_and_continue
import gamerlogue.sharedui.generated.resources.library__save_before_review_message
import gamerlogue.sharedui.generated.resources.library__save_before_review_title
import gamerlogue.sharedui.generated.resources.library__status
import gamerlogue.sharedui.generated.resources.library__status_description
import io.github.kingsword09.symbolcraft.symbols.icons.materialsymbols.Icons
import io.github.kingsword09.symbolcraft.symbols.icons.materialsymbols.icons.CategoryW500Rounded
import io.github.kingsword09.symbolcraft.symbols.icons.materialsymbols.icons.DeleteW500Rounded
import io.github.kingsword09.symbolcraft.symbols.icons.materialsymbols.icons.RateReviewW500Rounded
import io.github.kingsword09.symbolcraft.symbols.icons.materialsymbols.icons.TrophyW500Rounded
import it.maicol07.gamerlogue.auth.AuthTokenProvider
import it.maicol07.gamerlogue.data.LibraryEntry
import it.maicol07.gamerlogue.data.LibraryEntrySchema
import it.maicol07.gamerlogue.ui.components.ButtonProgress
import it.maicol07.gamerlogue.ui.components.SingleSelectConnectedButtonGroup
import it.maicol07.gamerlogue.ui.components.TooltipBox
import it.maicol07.gamerlogue.ui.views.library.GameLibraryStatus
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** The platforms the signed-in user owns, from their profile, to put first in the sheet. */
@Composable
private fun ownedPlatformIds(): List<Int> {
    val session by koinInject<AuthTokenProvider>().session.collectAsStateWithLifecycle()
    return session.user?.platformsIds.orEmpty()
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GameAddEditLibrarySheet(
    game: Game,
    existingData: LibraryEntry? = null,
    onDismiss: () -> Unit,
    ownedPlatformIds: List<Int> = ownedPlatformIds(),
    viewModel: AddToLibrarySheetViewModel = koinViewModel(
        key = "${game.id}_${existingData.hashCode()}",
    ) { parametersOf(game, existingData, ownedPlatformIds) },
    onDelete: () -> Unit = {},
    /** Opens the review editor on the entry with this id, saved just before. */
    onWriteReview: (String) -> Unit,
    onSave: (LibraryEntry) -> Unit
) {
    val status = viewModel.selectedStatus
    val isBacklog = status == GameLibraryStatus.BACKLOG
    val isPlayingOrPaused = status == GameLibraryStatus.PLAYING || status == GameLibraryStatus.PAUSED
    val isEditing = existingData != null

    // Dismissing by dropping the sheet out of composition skips the slide-out; every programmatic
    // close (cancel, save, delete) has to animate first and only then let the caller remove it.
    val sheetState = rememberBottomSheetState(SheetValue.Hidden)
    val scope = rememberCoroutineScope()
    val dismiss: () -> Unit = {
        scope.launch { sheetState.hide() }.invokeOnCompletion { onDismiss() }
    }
    var confirmClearReview by remember { mutableStateOf(false) }
    var confirmSaveForReview by remember { mutableStateOf(false) }
    val openReview: (String) -> Unit = {
        onWriteReview(it)
        dismiss()
    }
    val save: () -> Unit = {
        viewModel.saveEntry {
            onSave(it)
            dismiss()
        }
    }

    ModalBottomSheet(onDismiss, sheetState = sheetState) {
        LazyColumn(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .navigationBarsPadding()
                .imePadding(),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            statusSection(game.name, viewModel, isEditing)
            item {
                // Kept enabled while set, so an entry owned before the release can still be unset.
                OwnedSwitch(
                    owned = viewModel.owned,
                    enabled = viewModel.released || viewModel.owned,
                    onOwnedChange = { viewModel.owned = it }
                )
            }
            item { EditionSection(game, viewModel) }
            item { PlatformSection(viewModel) }
            if (!isBacklog) {
                item {
                    Column(modifier = Modifier.animateItem()) {
                        DatesSection(viewModel, showEndDate = !isPlayingOrPaused)
                    }
                }
                item {
                    Box(modifier = Modifier.animateItem()) {
                        PlayedTimeField(viewModel)
                    }
                }
            }

            if (!isBacklog && !isPlayingOrPaused) {
                if (!viewModel.detailedReview) {
                    item {
                        Column(modifier = Modifier.animateItem()) {
                            RatingSection(
                                rating = viewModel.rating,
                                onRatingChange = { viewModel.rating = it }
                            )
                        }
                    }
                }
                item {
                    Box(modifier = Modifier.animateItem()) {
                        ReviewRow(existingData, enabled = !viewModel.saveLoading) {
                            if (viewModel.hasChanges) confirmSaveForReview = true else existingData?.id?.let(openReview)
                        }
                    }
                }
            }

            if (viewModel.error != null) {
                item {
                    Text(
                        text = stringResource(viewModel.error!!),
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.fillMaxWidth().animateItem()
                    )
                }
            }

            item {
                ActionsRow(
                    saveLoading = viewModel.saveLoading,
                    deleteLoading = viewModel.deleteLoading,
                    isEditing = isEditing,
                    onDismiss = dismiss,
                    onDelete = {
                        viewModel.deleteEntry {
                            onDelete()
                            dismiss()
                        }
                    },
                    onSave = { if (viewModel.clearsReview) confirmClearReview = true else save() }
                )
            }
        }
        Spacer(modifier = Modifier.height(16.dp))
    }
    if (confirmClearReview) {
        ConfirmDialog(
            icon = Icons.DeleteW500Rounded,
            title = Res.string.library__clear_review_title,
            message = Res.string.library__clear_review_message,
            confirmLabel = Res.string.library__clear_review_confirm,
            destructive = true,
            onConfirm = save,
            onDismiss = { confirmClearReview = false }
        )
    }
    if (confirmSaveForReview) {
        ConfirmDialog(
            icon = Icons.RateReviewW500Rounded,
            title = Res.string.library__save_before_review_title,
            message = Res.string.library__save_before_review_message,
            confirmLabel = Res.string.library__save_and_continue,
            destructive = false,
            onConfirm = { viewModel.saveForReview(openReview) },
            onDismiss = { confirmSaveForReview = false }
        )
    }
}

@Composable
private fun ConfirmDialog(
    icon: ImageVector,
    title: StringResource,
    message: StringResource,
    confirmLabel: StringResource,
    destructive: Boolean,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit
) = AlertDialog(
    onDismissRequest = onDismiss,
    icon = { Icon(icon, contentDescription = null) },
    title = { Text(stringResource(title)) },
    text = { Text(stringResource(message)) },
    confirmButton = {
        TextButton(
            onClick = {
                onDismiss()
                onConfirm()
            },
            shapes = ButtonDefaults.shapes(),
            colors = if (destructive) {
                ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
            } else {
                ButtonDefaults.textButtonColors()
            },
        ) { Text(stringResource(confirmLabel)) }
    },
    dismissButton = {
        TextButton(onClick = onDismiss, shapes = ButtonDefaults.shapes()) {
            Text(stringResource(Res.string.library__cancel))
        }
    },
)

private fun LazyListScope.statusSection(
    gameName: String,
    viewModel: AddToLibrarySheetViewModel,
    isEditing: Boolean
) {
    item {
        Text(
            text = stringResource(
                Res.string.run {
                    if (isEditing) library__edit_entry else library__add_to_library
                },
                gameName
            ),
            style = MaterialTheme.typography.headlineSmall
        )
    }
    item {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.CategoryW500Rounded,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        text = stringResource(Res.string.library__status),
                        style = MaterialTheme.typography.titleMedium
                    )
                }
                Text(
                    text = stringResource(Res.string.library__status_description),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            LibraryStatusSelector(
                selectedStatus = viewModel.selectedStatus,
                released = viewModel.released,
                onSectionStatus = { viewModel.selectedStatus = it }
            )
        }
    }

    // Completion status (only for the COMPLETED section)
    if (viewModel.selectedStatus == GameLibraryStatus.COMPLETED) {
        item("completion_status") {
            Column(
                modifier = Modifier.animateItem(),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        imageVector = Icons.TrophyW500Rounded,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        text = stringResource(Res.string.library__completion_status),
                        style = MaterialTheme.typography.titleMedium
                    )
                }
                Text(
                    text = stringResource(Res.string.library__completion_status_description),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
            CompletionStatusChips(
                selectedStatus = viewModel.completionStatus,
                onStatusSelected = { viewModel.completionStatus = it },
                modifier = Modifier.animateItem()
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun LibraryStatusSelector(
    selectedStatus: GameLibraryStatus?,
    released: Boolean,
    onSectionStatus: (GameLibraryStatus) -> Unit
) {
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        for (section in GameLibraryStatus.entries) {
            TooltipBox(
                tooltip = { PlainTooltip { Text(stringResource(section.displayName)) } }
            ) {
                FilledIconToggleButton(
                    checked = selectedStatus == section,
                    onCheckedChange = { onSectionStatus(section) },
                    // The current status stays enabled, so an existing entry is never locked out.
                    enabled = released || section == GameLibraryStatus.BACKLOG || section == selectedStatus,
                    shapes = IconButtonDefaults.toggleableShapes(),
                    colors = IconButtonDefaults.filledIconToggleButtonColors(
                        containerColor = MaterialTheme.colorScheme.surfaceVariant,
                        contentColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        checkedContainerColor = MaterialTheme.colorScheme.primary,
                        checkedContentColor = MaterialTheme.colorScheme.onPrimary,
                    )
                ) {
                    Icon(
                        imageVector = section.icon,
                        contentDescription = stringResource(section.displayName)
                    )
                }
            }
        }
    }
}

@Composable
private fun CompletionStatusChips(
    selectedStatus: LibraryEntrySchema.CompletionStatus?,
    onStatusSelected: (LibraryEntrySchema.CompletionStatus?) -> Unit,
    modifier: Modifier = Modifier
) {
    SingleSelectConnectedButtonGroup(
        options = LibraryEntrySchema.CompletionStatus.entries,
        selected = selectedStatus,
        onSelectedChange = onStatusSelected,
        toggleButtonText = { stringResource(it.displayName) },
        showChecks = true,
        containerColor = MaterialTheme.colorScheme.surfaceVariant,
        modifier = modifier
    )
}

@Composable
private fun ActionsRow(
    saveLoading: Boolean = false,
    deleteLoading: Boolean = false,
    isEditing: Boolean,
    onDismiss: () -> Unit,
    onDelete: () -> Unit,
    onSave: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        TextButton(
            shapes = ButtonDefaults.shapes(),
            onClick = onDismiss
        ) {
            Text(stringResource(Res.string.library__cancel))
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            if (isEditing) {
                TextButton(
                    shapes = ButtonDefaults.shapes(),
                    enabled = !saveLoading && !deleteLoading,
                    colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error),
                    onClick = onDelete
                ) {
                    ButtonProgress(deleteLoading) {
                        Text(stringResource(Res.string.library__delete))
                    }
                }
            }
            Button(
                shapes = ButtonDefaults.shapes(),
                enabled = !saveLoading && !deleteLoading,
                onClick = onSave
            ) {
                ButtonProgress(saveLoading) {
                    Text(stringResource(Res.string.library__save))
                }
            }
        }
    }
}
